import { Popover, PopoverTrigger, PopoverContent } from './chunk-QOM4QXAM.js';
import { Input } from './chunk-NHXI3UAE.js';
import { useFullscreenPortal } from './chunk-MBTLYAMU.js';
import { buttonVariants } from './chunk-PLP233UC.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React8 from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check, Pipette } from 'lucide-react';
import { cva } from 'class-variance-authority';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { readableForeground, normalizeHex, isHexColor, CATALOG_PALETTE } from '@authe/fe-commons/color';

var Switch = React8.forwardRef(function Switch2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SwitchPrimitive.Root,
    {
      ref,
      className: cn(
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand data-[state=unchecked]:bg-input focus-ring",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SwitchPrimitive.Thumb,
        {
          className: cn(
            "pointer-events-none block size-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
          )
        }
      )
    }
  );
});
function ViewModeToggle({
  options,
  value,
  onChange,
  size = "md",
  className,
  ...rest
}) {
  const buttons = React8.useRef(/* @__PURE__ */ new Map());
  const enabled = options.filter((option) => !option.disabled);
  const tabStop = enabled.some((option) => option.value === value) ? value : enabled[0]?.value;
  const pad = size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-2.5 text-sm";
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "radiogroup",
      "aria-label": rest["aria-label"],
      className: cn("inline-flex items-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground", className),
      children: options.map((option) => {
        const active = option.value === value;
        return /* @__PURE__ */ jsxs(
          "button",
          {
            ref: (element) => {
              if (element) buttons.current.set(option.value, element);
              else buttons.current.delete(option.value);
            },
            type: "button",
            tabIndex: option.value === tabStop ? 0 : -1,
            onKeyDown: (event) => {
              if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) || !enabled.length) return;
              event.preventDefault();
              const current = enabled.findIndex((item) => item.value === option.value);
              const next = event.key === "Home" ? 0 : event.key === "End" ? enabled.length - 1 : (current + (event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1) + enabled.length) % enabled.length;
              const target = enabled[next];
              onChange(target.value);
              buttons.current.get(target.value)?.focus();
            },
            role: "radio",
            "aria-checked": active,
            disabled: option.disabled,
            title: option.title,
            onClick: () => !active && !option.disabled && onChange(option.value),
            className: cn(
              "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium ring-offset-background transition-all",
              "disabled:pointer-events-none disabled:opacity-50 focus-ring",
              pad,
              active ? "bg-background text-foreground shadow-sm" : "hover:text-foreground"
            ),
            children: [
              option.icon,
              option.label
            ]
          },
          option.value
        );
      })
    }
  );
}
function FilterChip({
  active,
  onClick,
  count,
  countUnknown = false,
  disabled = false,
  title,
  keepFocus = false,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      "aria-pressed": active,
      disabled,
      title,
      onMouseDown: keepFocus ? (e) => e.preventDefault() : void 0,
      onClick,
      className: cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        active ? "border-primary bg-primary/10 font-medium text-primary" : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
        className
      ),
      children: [
        children,
        count != null ? /* @__PURE__ */ jsx("span", { className: cn("tabular-nums", active ? "text-primary/70" : "text-muted-foreground/70"), children: count.toLocaleString() }) : countUnknown ? (
          // Not `aria-hidden`: a screen reader that skipped it would be back to
          // the silence this exists to break. `title` carries the reason for
          // both, which is why this branch is only reachable with one.
          /* @__PURE__ */ jsx("span", { className: cn("tabular-nums", active ? "text-primary/70" : "text-muted-foreground/70"), children: "\u2014" })
        ) : null
      ]
    }
  );
}
function Select({
  onValueChange,
  ...props
}) {
  const guarded = React8.useMemo(
    () => onValueChange ? (next) => {
      if (next !== "") onValueChange(next);
    } : void 0,
    [onValueChange]
  );
  return /* @__PURE__ */ jsx(SelectPrimitive.Root, { ...props, onValueChange: guarded });
}
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectLabel = React8.forwardRef(function SelectLabel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.Label,
    {
      ref,
      className: cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className),
      ...props
    }
  );
});
var SelectTrigger = React8.forwardRef(function SelectTrigger2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Trigger,
    {
      ref,
      className: cn(
        // `[&>span]:min-w-0 truncate`, not `line-clamp-1`: a line clamp cannot
        // shorten a `whitespace-nowrap` label in a flex row, so a long value ran
        // under the chevron (the Team page's switcher, on any long team name).
        "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 [&>span]:min-w-0 [&>span]:truncate focus-ring",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDown, { className: "size-4 opacity-50" }) })
      ]
    }
  );
});
var SelectContent = React8.forwardRef(function SelectContent2({ className, children, position = "popper", ...props }, ref) {
  const portalContainer = useFullscreenPortal();
  return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx(
    SelectPrimitive.Content,
    {
      ref,
      position,
      className: cn(
        // `min(...)` of the two caps, not the fixed 24rem alone: that cap is a
        // taste limit ("a select should not be half the screen") and says
        // nothing about whether there is 24rem of room. On a short window, or
        // a select opened near the bottom, the list overran the viewport and
        // `overflow-hidden` clipped the rest away. The available height is the
        // room Radix actually measured, so whichever is smaller wins.
        "relative z-50 max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out",
        position === "popper" && "data-[side=bottom]:translate-y-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SelectPrimitive.Viewport,
        {
          className: cn("p-1", position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]"),
          children
        }
      )
    }
  ) });
});
var SelectItem = React8.forwardRef(function SelectItem2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "absolute right-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "size-4" }) }) }),
        /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
      ]
    }
  );
});
var EMPTY_SENTINEL = "__empty__";
function sections(options) {
  const out = [];
  for (const option of options) {
    const last = out[out.length - 1];
    if (last && last.group === option.group) last.options.push(option);
    else out.push({ group: option.group, options: [option] });
  }
  return out;
}
function EnumSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  className,
  contentClassName,
  "aria-label": ariaLabel,
  title
}) {
  const offersEmpty = options.some((option) => option.value === "");
  const selectValue = value === "" ? offersEmpty ? EMPTY_SENTINEL : void 0 : value || void 0;
  const handleChange = (next) => onChange(next === EMPTY_SENTINEL ? "" : next);
  return /* @__PURE__ */ jsxs(Select, { value: selectValue, onValueChange: handleChange, disabled, children: [
    /* @__PURE__ */ jsx(SelectTrigger, { id, className, "aria-label": ariaLabel, title, children: /* @__PURE__ */ jsx(SelectValue, { placeholder }) }),
    /* @__PURE__ */ jsx(SelectContent, { className: contentClassName, children: sections(options).map((section, index) => {
      const items = section.options.map((option) => /* @__PURE__ */ jsx(
        SelectItem,
        {
          value: option.value === "" ? EMPTY_SENTINEL : option.value,
          disabled: option.disabled,
          children: option.label
        },
        option.value || EMPTY_SENTINEL
      ));
      return section.group === void 0 ? /* @__PURE__ */ jsx(React8.Fragment, { children: items }, `ungrouped-${index}`) : /* @__PURE__ */ jsxs(SelectGroup, { children: [
        /* @__PURE__ */ jsx(SelectLabel, { children: section.group }),
        items
      ] }, section.group);
    }) })
  ] });
}
var SIZES = {
  sm: "size-6 text-3xs",
  md: "size-8 text-xs",
  lg: "size-10 text-sm",
  xl: "size-14 text-base"
};
function Avatar({ src, alt, fallback, size = "md", className }) {
  const [failedSrc, setFailedSrc] = React8.useState();
  const showImage = src && failedSrc !== src;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-semibold text-muted-foreground",
        SIZES[size],
        className
      ),
      children: showImage ? /* @__PURE__ */ jsx(
        "img",
        {
          src,
          alt: alt ?? "",
          className: "block size-full object-cover",
          onError: () => setFailedSrc(src)
        }
      ) : fallback ?? "?"
    }
  );
}
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-current [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/40 bg-destructive/5 text-destructive",
        // The interface-state tokens, not a risk band or a finality state
        // borrowed for the colour: a warning here is not a medium-risk finding.
        warning: "border-warning/40 bg-warning/5 text-warning",
        success: "border-success/40 bg-success/5 text-success",
        info: "border-info/40 bg-info/5 text-info"
      }
    },
    defaultVariants: { variant: "default" }
  }
);
var Alert = React8.forwardRef(function Alert2({ className, variant, ...props }, ref) {
  return /* @__PURE__ */ jsx("div", { ref, role: "alert", className: cn(alertVariants({ variant }), className), ...props });
});
var AlertTitle = React8.forwardRef(
  function AlertTitle2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("h5", { ref, className: cn("mb-1 font-medium leading-none tracking-tight", className), ...props });
  }
);
var AlertDescription = React8.forwardRef(
  function AlertDescription2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("div", { ref, className: cn("text-sm [&_p]:leading-relaxed", className), ...props });
  }
);
var Slider = React8.forwardRef(function Slider2({ className, ...props }, ref) {
  const thumbCount = Array.isArray(props.value) ? props.value.length : Array.isArray(props.defaultValue) ? props.defaultValue.length : 1;
  return /* @__PURE__ */ jsxs(
    SliderPrimitive.Root,
    {
      ref,
      className: cn("relative flex w-full touch-none select-none items-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx(SliderPrimitive.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full bg-brand" }) }),
        Array.from({ length: thumbCount }).map((_, i) => /* @__PURE__ */ jsx(
          SliderPrimitive.Thumb,
          {
            className: "block size-4 rounded-full border border-brand/50 bg-background shadow-sm transition-colors disabled:pointer-events-none disabled:opacity-50 focus-ring"
          },
          i
        ))
      ]
    }
  );
});
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
function AlertDialogPortal({ container, ...props }) {
  const fullscreen = useFullscreenPortal();
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Portal, { container: container === void 0 ? fullscreen : container, ...props });
}
var AlertDialogOverlay = React8.forwardRef(function AlertDialogOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Overlay,
    {
      ref,
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
});
var AlertDialogContent = React8.forwardRef(function AlertDialogContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [
    /* @__PURE__ */ jsx(AlertDialogOverlay, {}),
    /* @__PURE__ */ jsx(
      AlertDialogPrimitive.Content,
      {
        ref,
        className: cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out sm:rounded-lg",
          className
        ),
        ...props
      }
    )
  ] });
});
function AlertDialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-y-2 text-center sm:text-left", className), ...props });
}
function AlertDialogFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });
}
var AlertDialogTitle = React8.forwardRef(function AlertDialogTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Title, { ref, className: cn("text-lg font-semibold", className), ...props });
});
var AlertDialogDescription = React8.forwardRef(function AlertDialogDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Description, { ref, className: cn("text-sm text-muted-foreground", className), ...props });
});
var AlertDialogAction = React8.forwardRef(function AlertDialogAction2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Action, { ref, className: cn(buttonVariants(), className), ...props });
});
var AlertDialogCancel = React8.forwardRef(function AlertDialogCancel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Cancel,
    {
      ref,
      className: cn(buttonVariants({ variant: "outline" }), "mt-2 sm:mt-0", className),
      ...props
    }
  );
});
var Collapsible = CollapsiblePrimitive.Root;
var CollapsibleTrigger2 = CollapsiblePrimitive.CollapsibleTrigger;
var CollapsibleContent2 = CollapsiblePrimitive.CollapsibleContent;
var SWATCH_SIZE = {
  sm: "size-5",
  md: "size-7",
  lg: "size-9"
};
function ColorSwatch({
  color,
  selected,
  onSelect,
  size = "md",
  title,
  className
}) {
  const fg = readableForeground(color);
  const Comp = onSelect ? "button" : "span";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      ...onSelect ? { type: "button", onClick: onSelect } : {},
      title: title ?? color,
      "aria-label": title ?? color,
      "aria-pressed": onSelect ? Boolean(selected) : void 0,
      className: cn(
        "inline-flex items-center justify-center rounded-md border border-black/10 shadow-xs transition-transform",
        onSelect && "cursor-pointer hover:scale-105 focus-ring",
        selected && "ring-2 ring-ring ring-offset-1",
        SWATCH_SIZE[size],
        className
      ),
      style: { backgroundColor: color },
      children: selected ? /* @__PURE__ */ jsx(Check, { className: "size-3.5", style: { color: fg } }) : null
    }
  );
}
function ColorPicker({
  value,
  onChange,
  palette = CATALOG_PALETTE.map((s) => s.hex),
  allowCustom = true,
  allowClear = true,
  disabled,
  placeholder = "Pick a colour",
  id,
  className
}) {
  const [open, setOpen] = React8.useState(false);
  const [draft, setDraft] = React8.useState(value ?? "");
  React8.useEffect(() => setDraft(value ?? ""), [value]);
  const normalized = normalizeHex(value);
  const commitCustom = () => {
    const next = normalizeHex(draft);
    if (next) {
      onChange(next);
      setOpen(false);
    }
  };
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: disabled ? void 0 : setOpen, children: [
    /* @__PURE__ */ jsxs(
      PopoverTrigger,
      {
        id,
        type: "button",
        disabled,
        className: cn(
          "inline-flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        children: [
          normalized ? /* @__PURE__ */ jsx("span", { className: "inline-block size-4 rounded-sm border border-black/10", style: { backgroundColor: normalized } }) : /* @__PURE__ */ jsx(Pipette, { className: "size-4 text-muted-foreground" }),
          /* @__PURE__ */ jsx("span", { className: cn(!normalized && "text-muted-foreground"), children: normalized ?? placeholder })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-60 space-y-3", children: [
      /* @__PURE__ */ jsx("div", { className: "grid grid-cols-6 gap-2", children: palette.map((color) => /* @__PURE__ */ jsx(
        ColorSwatch,
        {
          color,
          selected: normalizeHex(color) === normalized,
          onSelect: () => {
            onChange(normalizeHex(color));
            setOpen(false);
          }
        },
        color
      )) }),
      allowCustom && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            "aria-label": "Custom hex colour",
            value: draft,
            placeholder: "#rrggbb",
            onChange: (e) => setDraft(e.target.value),
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitCustom();
              }
            },
            className: cn("h-8 font-mono text-xs", draft && !isHexColor(draft) && "border-destructive")
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: commitCustom,
            disabled: !isHexColor(draft),
            className: "shrink-0 rounded-md border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50",
            children: "Set"
          }
        )
      ] }),
      allowClear && normalized && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => {
            onChange(null);
            setOpen(false);
          },
          className: "text-xs text-muted-foreground hover:text-foreground",
          children: "Clear colour"
        }
      )
    ] })
  ] });
}

export { Alert, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, Avatar, Collapsible, CollapsibleContent2 as CollapsibleContent, CollapsibleTrigger2 as CollapsibleTrigger, ColorPicker, ColorSwatch, EnumSelect, FilterChip, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, Slider, Switch, ViewModeToggle, alertVariants };
//# sourceMappingURL=chunk-MYIGL3HG.js.map
//# sourceMappingURL=chunk-MYIGL3HG.js.map